import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import { format, startOfDay, eachDayOfInterval, subDays } from 'date-fns';

const ActivityHeatMap = ({ data }) => {
  const heatMapData = useMemo(() => {
    if (!data || data?.length === 0) return [];

    const today = new Date();
    const startDate = subDays(today, 29); // Last 30 days
    const days = eachDayOfInterval({ start: startDate, end: today });

    // Count events per day
    const eventsByDay = {};
    data?.forEach(log => {
      const dayKey = format(startOfDay(new Date(log?.createdAt)), 'yyyy-MM-dd');
      eventsByDay[dayKey] = (eventsByDay?.[dayKey] || 0) + 1;
    });

    // Find max count for normalization
    const maxCount = Math.max(...Object.values(eventsByDay), 1);

    return days?.map(day => {
      const dayKey = format(day, 'yyyy-MM-dd');
      const count = eventsByDay?.[dayKey] || 0;
      const intensity = count / maxCount;

      return {
        date: day,
        dateKey: dayKey,
        count,
        intensity
      };
    });
  }, [data]);

  const getIntensityColor = (intensity) => {
    if (intensity === 0) return 'bg-muted';
    if (intensity < 0.25) return 'bg-primary/20';
    if (intensity < 0.5) return 'bg-primary/40';
    if (intensity < 0.75) return 'bg-primary/60';
    return 'bg-primary';
  };

  if (heatMapData?.length === 0) return null;

  return (
    <div className="bg-card rounded-xl border border-border p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="TrendingUp" size={20} strokeWidth={2} className="text-primary" />
        <h3 className="text-lg font-heading font-semibold text-foreground">
          Activity Heat Map (Last 30 Days)
        </h3>
      </div>

      <div className="space-y-2">
        <div className="grid grid-cols-10 sm:grid-cols-15 lg:grid-cols-30 gap-1">
          {heatMapData?.map((day, index) => (
            <div
              key={day?.dateKey}
              className={`aspect-square rounded ${getIntensityColor(day?.intensity)} transition-colors hover:ring-2 hover:ring-ring cursor-pointer`}
              title={`${format(day?.date, 'MMM dd, yyyy')}: ${day?.count} events`}
            />
          ))}
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2">
          <span>Less activity</span>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-muted" />
            <div className="w-3 h-3 rounded bg-primary/20" />
            <div className="w-3 h-3 rounded bg-primary/40" />
            <div className="w-3 h-3 rounded bg-primary/60" />
            <div className="w-3 h-3 rounded bg-primary" />
          </div>
          <span>More activity</span>
        </div>
      </div>
    </div>
  );
};

export default ActivityHeatMap;